/**
 * Microphone capture and assistant-audio playback helpers.
 *
 * Capture: getUserMedia -> AudioWorklet/ScriptProcessor -> PCM16 frames sent to
 * the agent as binary WS frames.
 * Playback: queue incoming PCM16 chunks and play them through an AudioContext.
 *
 * Nova Sonic uses 16 kHz mono PCM16 for mic input and 24 kHz mono PCM16 for
 * the assistant's audio output.
 */
const INPUT_SAMPLE_RATE = 16_000;
const OUTPUT_SAMPLE_RATE = 24_000;

/**
 * Mic ring-buffer budget retained while no socket is OPEN. Sized for the
 * sub-second proactive cutover; for a longer reactive gap it intentionally
 * retains only the most recent ~3s and drops oldest (ring). ~3s PCM16 @16kHz
 * mono = 96_000 bytes; same magnitude as the server's AUDIO_BUFFER_MAX_BYTES,
 * though the two cover different windows.
 */
export const MIC_BUFFER_MAX_BYTES = 96_000;

/**
 * Byte-bounded FIFO of mic PCM frames retained while no socket is OPEN (or
 * during a sub-second cutover), flushed into the new socket on open so no
 * spoken audio is lost. Ring semantics: drop oldest past the budget, keeping at
 * least the newest frame. Pure (no Web Audio), so it runs in the node test env.
 *
 * Mirrors the drop-oldest / keep-≥1 semantics of the server's audio ring buffer
 * (agent/src/session/session-controller.ts pushAudioBuffer).
 */
export class MicRingBuffer {
  private frames: ArrayBuffer[] = [];
  private bytes = 0;

  constructor(private readonly maxBytes = MIC_BUFFER_MAX_BYTES) {}

  /** Append a frame, dropping the OLDEST frames while over budget. Always keeps
   *  at least the just-pushed (newest) frame, even if it alone exceeds budget. */
  push(frame: ArrayBuffer): void {
    this.frames.push(frame);
    this.bytes += frame.byteLength;
    // Drop oldest while over budget, but never drop the newest (keep ≥1).
    while (this.bytes > this.maxBytes && this.frames.length > 1) {
      const dropped = this.frames.shift() as ArrayBuffer;
      this.bytes -= dropped.byteLength;
    }
  }

  /** Return all buffered frames (oldest→newest) and clear the buffer. */
  drain(): ArrayBuffer[] {
    const out = this.frames;
    this.frames = [];
    this.bytes = 0;
    return out;
  }

  /** Total bytes currently buffered. */
  get byteLength(): number {
    return this.bytes;
  }
}

export class MicCapture {
  private ctx: AudioContext | null = null;
  private stream: MediaStream | null = null;
  private source: MediaStreamAudioSourceNode | null = null;
  private processor: ScriptProcessorNode | null = null;

  constructor(private readonly onFrame: (pcm16: ArrayBuffer) => void) {}

  async start(): Promise<void> {
    this.stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    this.ctx = new AudioContext({ sampleRate: INPUT_SAMPLE_RATE });
    // A suspended context (browser autoplay policy) produces no audio frames —
    // resume it so mic capture actually flows.
    if (this.ctx.state === 'suspended') {
      try { await this.ctx.resume(); } catch { /* best effort */ }
    }
    this.source = this.ctx.createMediaStreamSource(this.stream);
    // ScriptProcessor is deprecated but universally available; an AudioWorklet
    // is the production upgrade path.
    this.processor = this.ctx.createScriptProcessor(4096, 1, 1);
    this.processor.onaudioprocess = (e) => {
      const input = e.inputBuffer.getChannelData(0);
      this.onFrame(floatToPcm16(input));
    };
    this.source.connect(this.processor);
    this.processor.connect(this.ctx.destination);
  }

  stop(): void {
    this.processor?.disconnect();
    this.source?.disconnect();
    this.stream?.getTracks().forEach((t) => t.stop());
    void this.ctx?.close();
    this.ctx = this.stream = this.source = this.processor = null;
  }
}

/**
 * Delay (ms) after the last scheduled source ends before declaring the player
 * idle. This short tail absorbs the gap between back-to-back audio chunks so
 * the "speaking" signal does not flicker off between them; a new play() cancels
 * the pending idle transition.
 */
const SPEAKING_IDLE_DEBOUNCE_MS = 150;

export class AudioPlayer {
  private ctx: AudioContext | null = null;
  private nextStartTime = 0;
  /** Scheduled (possibly still-playing) sources, so barge-in can stop them. */
  private readonly active = new Set<AudioBufferSourceNode>();

  /** Subscribers notified on speaking transitions (append-only fan-out). */
  private readonly speakingSubs = new Set<(speaking: boolean) => void>();
  /** Last emitted speaking state; only actual transitions notify subscribers. */
  private speaking = false;
  /** Pending debounce for the speaking→false transition (see debounce const). */
  private idleTimer: ReturnType<typeof setTimeout> | null = null;

  ensure(): void {
    if (!this.ctx) {
      this.ctx = new AudioContext({ sampleRate: OUTPUT_SAMPLE_RATE });
      this.nextStartTime = this.ctx.currentTime;
    }
  }

  /** Enqueue a PCM16 chunk for gapless playback. */
  play(pcm16: ArrayBuffer): void {
    this.ensure();
    const ctx = this.ctx as AudioContext;
    const floats = pcm16ToFloat(pcm16);
    const buffer = ctx.createBuffer(1, floats.length, OUTPUT_SAMPLE_RATE);
    buffer.getChannelData(0).set(floats);
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    src.connect(ctx.destination);
    const startAt = Math.max(this.nextStartTime, ctx.currentTime);
    src.start(startAt);
    this.nextStartTime = startAt + buffer.duration;
    this.active.add(src);
    src.onended = () => {
      this.active.delete(src);
      // Last scheduled source ended → playback is finished. Always debounce the
      // transition to idle; do NOT gate on nextStartTime, which can retain a
      // small scheduling lead over currentTime even after audio has stopped.
      if (this.active.size === 0) {
        this.scheduleIdle();
      }
    };
    // A fresh chunk means we are speaking; cancel any pending idle transition
    // so back-to-back chunks don't flicker the signal off.
    this.clearIdleTimer();
    this.setSpeaking(true);
  }

  /**
   * Barge-in: immediately stop and discard all buffered/scheduled assistant
   * audio, but keep the AudioContext alive so the next turn plays normally.
   */
  flush(): void {
    for (const src of this.active) {
      try { src.stop(); } catch { /* already stopped */ }
      try { src.disconnect(); } catch { /* ignore */ }
    }
    this.active.clear();
    if (this.ctx) this.nextStartTime = this.ctx.currentTime;
    // Barge-in ends speaking immediately (no tail).
    this.clearIdleTimer();
    this.setSpeaking(false);
  }

  stop(): void {
    this.flush();
    void this.ctx?.close();
    this.ctx = null;
    this.nextStartTime = 0;
  }

  /**
   * True while any scheduled source is active or playing. `active` holds every
   * scheduled-but-not-yet-ended source, so it is the reliable signal: true
   * exactly while audio is scheduled/playing, false once all sources have
   * ended. (We intentionally do NOT consult nextStartTime, which can retain a
   * stale lead over currentTime and latch this true.)
   */
  isSpeaking(): boolean {
    return this.active.size > 0;
  }

  /**
   * Subscribe to speaking transitions. Fires only on actual boolean changes,
   * not on every chunk. Returns an unsubscribe function.
   */
  onSpeakingChange(cb: (speaking: boolean) => void): () => void {
    this.speakingSubs.add(cb);
    return () => {
      this.speakingSubs.delete(cb);
    };
  }

  /** Emit only on a real transition, then notify subscribers. */
  private setSpeaking(next: boolean): void {
    if (this.speaking === next) return;
    this.speaking = next;
    for (const cb of this.speakingSubs) {
      try { cb(next); } catch { /* a bad subscriber must not break others */ }
    }
  }

  /** Schedule the debounced speaking→false transition if still idle when it fires. */
  private scheduleIdle(): void {
    this.clearIdleTimer();
    this.idleTimer = setTimeout(() => {
      this.idleTimer = null;
      // If nothing was rescheduled during the debounce, we're idle. Reset
      // nextStartTime to the current clock so no stale scheduling lead lingers.
      if (this.active.size === 0) {
        this.nextStartTime = this.ctx?.currentTime ?? 0;
        this.setSpeaking(false);
      }
    }, SPEAKING_IDLE_DEBOUNCE_MS);
  }

  private clearIdleTimer(): void {
    if (this.idleTimer !== null) {
      clearTimeout(this.idleTimer);
      this.idleTimer = null;
    }
  }
}

function floatToPcm16(input: Float32Array): ArrayBuffer {
  const out = new Int16Array(input.length);
  for (let i = 0; i < input.length; i++) {
    const s = Math.max(-1, Math.min(1, input[i] ?? 0));
    out[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }
  return out.buffer;
}

function pcm16ToFloat(buffer: ArrayBuffer): Float32Array {
  const view = new Int16Array(buffer);
  const out = new Float32Array(view.length);
  for (let i = 0; i < view.length; i++) {
    out[i] = (view[i] ?? 0) / 0x8000;
  }
  return out;
}
